import type { ColumnDef, Row } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import EnvelopeIcon from '~/components/EnvelopeIcon.vue'

export const createColumns = (
  companyId: number | string,
  queryParams: ProgressTrackerFilterParams | null = null,
  canEdit?: boolean,
): ColumnDef<PendingApproval>[] => {
  const columns: ColumnDef<PendingApproval>[] = [
    {
      accessorKey: 'pending_approver',
      accessorFn: row => `${row.pending_approver?.first_name} ${row.pending_approver?.last_name ?? ''}`,
      id: 'pending_approver.name',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Approver Name'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc', true),
          }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'capitalize text-left text-md text-foreground no-localize-translate' }, row.getValue(column.id)),
    },
    {
      accessorKey: 'submission_id',
      id: 'submission_id',
      header: () => h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Submission ID'),
      cell: ({ row, column }) => {
        return h(Button, {
          class: 'underline text-foreground text-md',
          variant: 'link',
          onClick: () => navigateTo('/dashboard/data-upload/submissions?status=pending'),
        }, () => [row.getValue(column.id)])
      },
    },
    {
      accessorKey: 'submission_date',
      id: 'submission_date',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Submission Date'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => {
        const { $moment } = useNuxtApp()
        const formatted = $moment(row.getValue(column.id)).format('DD/MM/yyyy')
        return h('div', { class: 'capitalize text-left text-md text-foreground' }, formatted)
      },
    },
  ]

  if (canEdit !== false) {
    columns.push({
      id: 'actions',
      header: () => h('span', { class: 'max-w-min text-sm text-slate-800 font-semibold' }, 'Actions'),
      cell: ({ row }) => {
        return h(Button, {
          class: 'text-main-primary',
          variant: 'link',
          onClick: () => sendIndividualEmail(row, companyId, queryParams),
        }, () => h(EnvelopeIcon))
      },
    })
  }

  return columns
}

const sendIndividualEmail = (row: Row<PendingApproval>, companyId: number | string, queryParams: ProgressTrackerFilterParams | null) => {
  const { protocol, host } = window.location
  const { $apis, $toast } = useNuxtApp()
  const submission_id = getSubmissionId(row)
  const submission_link = `${protocol}//${host}${generateSubmissionPath(row)}`

  const params: TrackerEmailReminderParams = {
    action: 'pending_approvals',
    recipient_id: row.original.pending_approver.id,
    submission_id,
    redirect_link: `${protocol}//${host}/dashboard/ghg-approval`,
    submission_link,
    ...(queryParams && { start_date: queryParams.start_date, end_date: queryParams.end_date }),
  }

  $apis.progressTracker
    .sendEmailReminder(companyId, params)
    .then(() => $toast.success(`Processing an email notification to ${row.original.pending_approver.first_name}`))
}

const generateSubmissionPath = (row: Row<PendingApproval>) => {
  const { $buffer } = useNuxtApp()

  const encodedCategory = $buffer.from(row.original.category.toString(), 'utf-8').toString('base64')
  const encodedApprovals = row.original.all_approvers
    .map(approval => $buffer.from(approval.toString(), 'utf-8').toString('base64'))
    .join(',')
  const encodedId = $buffer.from(getSubmissionId(row).toString(), 'utf-8').toString('base64')

  return `/dashboard/data-upload/review/${encodedCategory}?approvals=${encodedApprovals}&previous-submission=${encodedId}`
}

const getSubmissionId = (row: Row<PendingApproval>) => {
  return parseInt(row.original.submission_id.replace('SC-', ''))
}
